import { useState, useEffect, useCallback } from 'react';
import { AxiosInstance, AxiosRequestConfig } from 'axios';

interface UseApiOptions<T> {
  initialData: T;
  debounceTime?: number;
  validateInput?: (params: any) => boolean;
}

function shallowEqual(objA: any, objB: any) {
  if (objA === objB) return true;
  if (!objA || !objB) return false;
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) return false;
  for (let key of keysA) {
    if (objA[key] !== objB[key]) return false;
  }
  return true;
}

export function useApi<T, P = any>(
  apiClient: AxiosInstance,
  options: UseApiOptions<T>
) {
  const [data, setData] = useState<T>(options.initialData);
  const [params, _setParams] = useState<P | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const debounceTime = options.debounceTime || 500;

  // Only update params if they are different
  const setParams = (newParams: P) => {
    _setParams(prev => (shallowEqual(prev, newParams) ? prev : newParams));
  };

  const fetchData = useCallback(
    async (requestParams: P, config?: AxiosRequestConfig) => {
      if (options.validateInput && !options.validateInput(requestParams)) {
        setError(new Error('Invalid input parameters'));
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.get('', {
          params: requestParams,
          ...config,
        });
        setData(response.data);
      } catch (err: any) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    },
    [apiClient, options]
  );

  useEffect(() => {
    if (!params) return;
    const timeoutId = setTimeout(() => {
      fetchData(params);
    }, debounceTime);
    return () => clearTimeout(timeoutId);
  }, [params, debounceTime, fetchData]);

  return { data, setParams, isLoading, error };
}